import type { ApiClient } from './client';
import { clock, dayMonth, dayWord, detailWhen, rowWhen, weekdays } from './productReports';

/**
 * The organization's alert inbox, as `veri-plus-backend` serves it.
 *
 * `routes/v1/alert.routes.ts`: `GET /v1/alerts` for any member, pagination
 * plus `unread` as the strings `true` or `false`, answering
 * `{ alerts, nextCursor, unreadCount }`; and `POST /v1/alerts/:alertId/read`,
 * answering 204. The read is idempotent upstream, so a retry is safe. The
 * alert is `alert.model.ts` through its `toJSON`. `unreadCount` is counted
 * over the whole organization whatever the query filtered.
 *
 * The board draws no pager, so the screen loads every page and filters
 * locally, as the reports inbox does (D13). It never sends `unread`, so the
 * backend's `unread=false` trap cannot be reached from here. D14.
 */

export type AlertType = 'suspect_scan' | 'consumer_report' | 'out_of_region' | 'geo_spike';
export type AlertSeverity = 'warning' | 'critical';

export interface Alert {
  id: string;
  /** Per organization, drawn `#A-<number>`. Absent on alerts older than the field. */
  number?: number;
  type: AlertType;
  severity: AlertSeverity;
  title: string;
  message: string;
  occurrences: number;
  firstSeenAt: string;
  lastSeenAt: string;
  /** GeoJSON, `[lng, lat]`, on a geo spike. */
  location?: { type: 'Point'; coordinates: [number, number] };
  /** A geo spike's latest window. */
  spike?: { scans: number; distinctCodes: number; distinctUsers: number };
  /** Absent when the scan had no location, null when the registry named no town. */
  place?: { label: string; countryCode: string } | null;
  productId?: string;
  readAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface AlertPage {
  alerts: Alert[];
  nextCursor: string | null;
  unreadCount: number;
}

/** The API's own ceiling, `paginationQuery` in `validation/common.ts`. */
const pageLimit = 100;

export interface AlertInbox {
  alerts: Alert[];
  unreadCount: number;
}

/** Follows `nextCursor` until it runs out. A cursor that repeats ends the walk. */
export async function listAllAlerts(api: ApiClient, signal?: AbortSignal): Promise<AlertInbox> {
  const alerts: Alert[] = [];
  let unreadCount: number;
  let cursor: string | null = null;
  const seen = new Set<string>();
  do {
    const query: string = cursor === null ? '' : `&cursor=${encodeURIComponent(cursor)}`;
    const page: AlertPage = await api.get<AlertPage>(
      `/v1/alerts?limit=${pageLimit}${query}`,
      signal ? { signal } : {},
    );
    alerts.push(...page.alerts);
    unreadCount = page.unreadCount;
    cursor = page.nextCursor ?? null;
    if (cursor !== null) {
      if (seen.has(cursor)) break;
      seen.add(cursor);
    }
  } while (cursor !== null);
  return { alerts, unreadCount };
}

/** The shell's badge read, exported so a screen test's fake can answer it and keep its own reads exact. */
export const unreadCountPath = '/v1/alerts?limit=1';

/** The sidebar badge: the list route's `unreadCount`, for the price of one alert. */
export async function getUnreadCount(api: ApiClient, signal?: AbortSignal): Promise<number> {
  const page = await api.get<AlertPage>(unreadCountPath, signal ? { signal } : {});
  return page.unreadCount;
}

export async function markAlertRead(api: ApiClient, alertId: string): Promise<void> {
  await api.post<unknown>(`/v1/alerts/${encodeURIComponent(alertId)}/read`);
}

export type AlertFilter = 'all' | 'unread' | 'critical';

/** The board's three pills. */
export function inAlertFilter(alert: Alert, filter: AlertFilter): boolean {
  if (filter === 'unread') return alert.readAt === undefined;
  if (filter === 'critical') return alert.severity === 'critical';
  return true;
}

/**
 * The board draws a window, `14:02–17:31`, on the geo spike alone, and a
 * single time on every other row, occurrences or not. So a spike shows the
 * clock of its first and last sighting and the rest show the last. D14.
 */
function spanOf(alert: Alert): string | null {
  if (alert.type !== 'geo_spike' || alert.occurrences < 2) return null;
  const first = new Date(alert.firstSeenAt);
  const last = new Date(alert.lastSeenAt);
  if (first.toDateString() !== last.toDateString()) return null;
  return `${clock(first)}–${clock(last)}`;
}

/** A row's time: the spike's window on its day, `today 08:12`, `yesterday`, `8 Sep`. */
export function alertRowWhen(alert: Alert, now: Date): string {
  const span = spanOf(alert);
  if (span === null) return rowWhen(alert.lastSeenAt, now);
  const last = new Date(alert.lastSeenAt);
  const word = dayWord(last, now);
  return word === 'today' ? span : `${span} ${word ?? dayMonth(last)}`;
}

/** The detail's time: `14:02–17:31 today` is drawn; the rest follow the reports inbox. */
export function alertDetailWhen(alert: Alert, now: Date): string {
  const span = spanOf(alert);
  if (span === null) return detailWhen(alert.lastSeenAt, now);
  const last = new Date(alert.lastSeenAt);
  return `${span} ${dayWord(last, now) ?? dayMonth(last)}`;
}

/** `#A-2214`, or null on an alert older than the field. */
export const alertNumber = (alert: Alert): string | null =>
  alert.number === undefined ? null : `#A-${alert.number}`;

/**
 * Where it happened: the town, or a spike's cell by its own coordinates where
 * no town resolved, as the dashboard draws a place (D39). Null with neither. D40.
 */
export function alertPlace(alert: Alert): string | null {
  if (alert.place) return alert.place.label;
  const at = alert.location?.coordinates;
  return at ? `${at[1].toFixed(1)}, ${at[0].toFixed(1)}` : null;
}

/** A row's caption: `Product · near Nairobi CBD · 14:02–17:31`, `near` on a spike alone as drawn. */
export function alertCaption(alert: Alert, product: string | undefined, when: string): string {
  const place = alertPlace(alert);
  const where = place !== null && alert.type === 'geo_spike' ? `near ${place}` : place;
  return [product || null, where, when].filter((part) => part !== null).join(' · ');
}

/** The newest few, one page: the dashboard's Latest alerts. */
export async function latestAlerts(
  api: ApiClient,
  limit: number,
  signal?: AbortSignal,
): Promise<Alert[]> {
  const page = await api.get<AlertPage>(`/v1/alerts?limit=${limit}`, signal ? { signal } : {});
  return page.alerts;
}

/**
 * The dashboard's alert time: `08:12`, `Yesterday` and `Mon` are drawn, read
 * as today, yesterday and the six days before. Older takes the inbox's `8 Sep`.
 */
export function dashboardAlertWhen(alert: Alert, now: Date): string {
  const at = new Date(alert.lastSeenAt);
  const word = dayWord(at, now);
  if (word === 'today') return clock(at);
  if (word === 'yesterday') return 'Yesterday';
  const start = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((start(now) - start(at)) / 86_400_000);
  return days > 1 && days < 7 ? (weekdays[at.getDay()] ?? dayMonth(at)) : dayMonth(at);
}
